import { Drawer } from '@zorro/design-system/v2/Drawer';
import { useAppRouter, useMonolithQuery } from '@zorro/web/ui-utils';

import { CarrierApplicationModal } from '../../../CarrierApplicationFlow';
import {
  AssignEnrollmentForm,
  ChangeElectionWindowForm,
  ChangeEmployeeCustomClassCategoryForm,
  DeleteEnrollmentRecord,
  EditEnrollmentsRecord,
  EmployeeActionsOverlays,
  LogEnrollmentCallForm,
  ResetElectionDialog,
  WaiveCoverageForm,
  WaiveCoverageFormSingle,
  mapEmployeeForActions,
} from '../../../EmployeeActions';
import { EnrollmentConfirmationModal } from '../../../EnrollmentConfirmationFlow';
import { ManualSubmissionModal } from '../../../ManualSubmissionFlow';
import { EmployeePageTabType } from '../../EmployeePageContent';
import { EmployeePagePlansBenefitCardDentalPlanElectionDrawer } from '../../EmployeePageContent/EmployeePageTabs/EmployeePagePlans/EmployeePagePlansBenefitCardPlanElectionDrawer/EmployeePagePlansBenefitCardDentalPlanElectionDrawer';
import { EmployeePagePlansBenefitCardVisionPlanElectionDrawer } from '../../EmployeePageContent/EmployeePageTabs/EmployeePagePlans/EmployeePagePlansBenefitCardPlanElectionDrawer/EmployeePagePlansBenefitCardVisionPlanElectionDrawer';
import { EditAllowanceDrawer } from './EditAllowanceDrawer';
import { EnrollmentAction } from './EmployeePageActionsMenuUtils';

type Props = {
  action?: EnrollmentAction;
  onClose: () => void;
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  isDon: boolean;
  tab: EmployeePageTabType;
};

export const EmployeePageActionsOverlays = ({
  isDon,
  action,
  onClose,
  employerId,
  employeeId,
  onboardingPeriodId,
  tab,
}: Props) => {
  const { navigateToRoute } = useAppRouter();

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  if (!onboardingPeriod || !employee) {
    return null;
  }

  const recordForActions = mapEmployeeForActions(
    employee,
    onboardingPeriodId,
    onboardingPeriod?.isWaived
  );

  const hasNonWaivedRecords = !recordForActions.isOnboardingWaived;

  if (action === EnrollmentAction.MANUAL_SUBMISSION) {
    return (
      <ManualSubmissionModal
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        onClose={onClose}
        isDon={isDon}
        isOpen
      />
    );
  }

  if (action === EnrollmentAction.CARRIER_APPLICATION) {
    return (
      <CarrierApplicationModal
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        onClose={onClose}
        isOpen
        tab={tab}
      />
    );
  }

  if (action === EnrollmentAction.ENROLLMENT_CONFIRMATION) {
    return (
      <EnrollmentConfirmationModal
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        onClose={onClose}
        isOpen
      />
    );
  }

  return (
    <>
      <Drawer
        opened={action === EnrollmentAction.EDIT_ENROLLMENT_RECORD}
        onClose={onClose}
        title="Edit enrollment record"
      >
        <EditEnrollmentsRecord
          onSuccess={onClose}
          employerId={employerId}
          recordsForActions={[recordForActions]}
        />
      </Drawer>

      <Drawer
        opened={action === EnrollmentAction.ASSIGN_ENROLLMENT}
        onClose={onClose}
        title="Assign enrollment"
      >
        <AssignEnrollmentForm
          onClose={onClose}
          employerId={employerId}
          recordsForActions={[recordForActions]}
        />
      </Drawer>

      <DeleteEnrollmentRecord
        isOpen={action === EnrollmentAction.DELETE_ENROLLMENT_RECORD}
        onClose={onClose}
        onboardingPeriodId={onboardingPeriodId}
        onSubmit={() =>
          navigateToRoute('OPS_EMPLOYEE_PAGE', [{ employerId, employeeId }])
        }
      />

      <Drawer
        opened={action === EnrollmentAction.CHANGE_ELECTION_WINDOW}
        onClose={onClose}
        title="Change election window"
      >
        <ChangeElectionWindowForm
          planYearId={onboardingPeriod.enrollmentPeriodId}
          recordsForActions={[recordForActions]}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={action === EnrollmentAction.CHANGE_WAIVER_DETAILS}
        onClose={onClose}
        title="Change waiver details"
      >
        <WaiveCoverageFormSingle
          employeeId={employeeId}
          employerId={employerId}
          onboardingPeriodId={onboardingPeriodId}
          onSuccess={onClose}
          onClose={onClose}
        />
      </Drawer>

      <ResetElectionDialog
        isOpen={action === EnrollmentAction.RESET_ELECTION}
        recordsForActions={[recordForActions]}
        hasNonWaivedRecords={hasNonWaivedRecords}
        onClose={onClose}
        onSuccess={onClose}
      />

      <EditAllowanceDrawer
        isOpened={action === EnrollmentAction.EDIT_ALLOWANCE}
        onClose={onClose}
        onboardingPeriodId={onboardingPeriodId}
        currentAllowance={onboardingPeriod.allowance}
      />

      <Drawer
        opened={action === EnrollmentAction.CHANGE_CUSTOM_CLASS_CATEGORY}
        onClose={onClose}
        title={EmployeeActionsOverlays.CHANGE_EMPLOYEE_CUSTOM_CLASS}
      >
        <ChangeEmployeeCustomClassCategoryForm
          recordsForActions={[{ ...recordForActions, onboardingPeriodId }]}
          employerId={employerId}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={action === EnrollmentAction.WAIVE_COVERAGE}
        title="Waive coverage"
        onClose={onClose}
      >
        <WaiveCoverageForm
          recordsForActions={[recordForActions]}
          onSuccess={onClose}
          onClose={onClose}
        />
      </Drawer>

      <Drawer
        opened={action === EnrollmentAction.LOG_ENROLLMENT_CALL}
        onClose={onClose}
        title="Log enrollment call"
      >
        <LogEnrollmentCallForm
          onboardingPeriodId={onboardingPeriodId}
          onClose={onClose}
        />
      </Drawer>

      <EmployeePagePlansBenefitCardDentalPlanElectionDrawer
        isOpen={action === EnrollmentAction.ADD_DENTAL_PLAN}
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        onClose={onClose}
      />

      <EmployeePagePlansBenefitCardVisionPlanElectionDrawer
        isOpen={action === EnrollmentAction.ADD_VISION_PLAN}
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        onClose={onClose}
      />
    </>
  );
};
